/**
 * The convention list names are expected to follow.
 *
 * `[List #] – [Client Name] – [YYYY-MM] – [List Purpose]`
 *
 * It exists to stop duplicates and to keep a client's lists sorting and reading
 * together. It is a convention and not a validation: nothing rejects a name that
 * ignores it, and the database is full of names predating it ("First", "toto",
 * "TEST C4"). What the product does with it is read it; the Enrichment sessions
 * table breaks a conforming name over two lines, and this page teaches it.
 *
 * The separator is the en dash surrounded by spaces. `listNameLines`
 * (`features/enrichment/sessions/domain/list-name.domain.ts`) also accepts an em
 * dash and a plain hyphen when *reading* a name (being liberal in what it
 * parses) but there is one form the product hands out, and it is this one. Keep
 * the two in step: a format taught here that the parser cannot split would put
 * every new list on a single line in that table.
 */

export const NAMING_SEPARATOR = ' – ';

/** The placeholders, in order; the row of tokens the page displays. */
export const NAMING_FIELDS = ['[List #]', '[Client Name]', '[YYYY-MM]', '[List Purpose]'] as const;

/** The one string the copy affordance hands over. */
export const NAMING_FORMAT = NAMING_FIELDS.join(NAMING_SEPARATOR);

/**
 * A filled-in name, shown beside the format.
 *
 * The tokens alone do not settle the questions people actually have (whether
 * the number is padded, whether the month is the campaign's or today's) and one
 * example answers both without a sentence.
 */
export const NAMING_EXAMPLE = ['01', 'Acme Corp', '2025-08', 'Business Angels'].join(
  NAMING_SEPARATOR,
);

/**
 * The separator, as a matcher rather than a template.
 *
 * Liberal in what it accepts and strict in what it produces, like the sessions
 * table's parser: an en dash, an em dash or a plain hyphen, each surrounded by
 * spaces so a hyphenated word ("Jean-Marc", "Co-founders") is never a separator.
 * `NAMING_SEPARATOR` is the one form the product hands out.
 */
const SEPARATOR = /\s+[–—-]\s+/;

/** Below this many fields, a name is not following the convention. */
const CONVENTION_FIELDS = 4;

/** A real month, YYYY-MM. */
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Which of the four fields `name` already fills in the expected shape.
 *
 * A reading aid for the field helper, not a validation; the convention rejects
 * nothing. It reads the number the way `duplicateName` does (digits alone, so
 * "List #12" is not a number), wants a real month in the third field, and folds
 * anything past the fourth separator into the purpose. A `[placeholder]` left
 * over from the template counts as not filled in.
 */
export function namingFieldMatches(name: string): [boolean, boolean, boolean, boolean] {
  // A trailing separator (the suggestion ends on one) is not a field of its own.
  const fields = name.trim().replace(/\s+[–—-]$/, '').split(SEPARATOR).map((f) => f.trim());
  const filled = (field: string) => field !== '' && !/^\[.*\]$/.test(field);
  // Past four fields, a client may hold a dash: read around the month, as the
  // name mask does (`splitListName`); otherwise field by field.
  const monthAt =
    fields.length > CONVENTION_FIELDS ? fields.findIndex((f, i) => i >= 2 && MONTH.test(f)) : -1;
  const [number, client, month, purpose] =
    monthAt !== -1
      ? [
          fields[0],
          fields.slice(1, monthAt).join(NAMING_SEPARATOR),
          fields[monthAt],
          fields.slice(monthAt + 1).join(NAMING_SEPARATOR),
        ]
      : [
          fields[0] ?? '',
          fields[1] ?? '',
          fields[2] ?? '',
          fields.slice(CONVENTION_FIELDS - 1).join(NAMING_SEPARATOR),
        ];
  return [/^\d+$/.test(number), filled(client), MONTH.test(month), filled(purpose)];
}

/** A name's leading number, when it has one on its own ("07 – …" → 7). */
function leadingNumber(name: string): number | null {
  const fields = name.split(SEPARATOR);
  const first = fields[0]?.trim() ?? '';
  return fields.length > 1 && /^\d+$/.test(first) ? Number(first) : null;
}

/**
 * The number a new list of a licence should carry, given the names of the
 * licence's lists: one past the highest leading number (read as `duplicateName`
 * reads it, digits alone), and never below the count of lists, since lists
 * named before the convention are lists too. Padded to two digits, like the
 * example.
 */
export function nextListNumber(existingNames: readonly string[]): string {
  const highest = Math.max(
    existingNames.length,
    ...existingNames.map((n) => leadingNumber(n) ?? 0),
  );
  return String(highest + 1).padStart(2, '0');
}

/** The four parts of a name, in the convention's order. */
export type ListNameParts = [number: string, clientName: string, month: string, purpose: string];

/**
 * The four parts of `name`, for the New List name mask (#595, board E5).
 *
 * A name the mask wrote splits on the separator into exactly four, empty parts
 * kept in their place. Anything else (a pasted name, a client holding a dash)
 * is read around its month: the number before the client, the purpose after
 * the month, the client in between. A name with no month is read by position,
 * and one that is not in the convention at all goes whole into the purpose, so
 * pasting never loses a word.
 */
export function splitListName(name: string): ListNameParts {
  if (name.trim() === '') return ['', '', '', ''];
  // Untrimmed: a space typed at the end of a part is kept while the admin types.
  const exact = name.split(NAMING_SEPARATOR);
  if (exact.length === CONVENTION_FIELDS) return exact as ListNameParts;

  // A trailing separator (an old suggestion ends on one) is not a field of its own.
  const fields = name.trim().replace(/\s+[–—-]$/, '').split(SEPARATOR).map((f) => f.trim());
  if (fields.length === 1) return ['', '', '', fields[0]];
  const monthAt = fields.findIndex((f, i) => i >= 2 && MONTH.test(f));
  if (monthAt !== -1) {
    return [
      fields[0],
      fields.slice(1, monthAt).join(NAMING_SEPARATOR),
      fields[monthAt],
      fields.slice(monthAt + 1).join(NAMING_SEPARATOR),
    ];
  }
  return [
    fields[0],
    fields[1] ?? '',
    fields[2] ?? '',
    fields.slice(CONVENTION_FIELDS - 1).join(NAMING_SEPARATOR),
  ];
}

/** The name the four parts make; empty when they all are. Parts are joined as typed. */
export function joinListName(parts: readonly string[]): string {
  return parts.every((p) => p.trim() === '') ? '' : parts.join(NAMING_SEPARATOR);
}

/** The parts the form fills itself: number, licence, start month. The purpose stays empty. */
export function suggestedNameParts({
  number,
  clientName,
  startDate,
}: {
  number: string;
  clientName: string;
  startDate: string;
}): ListNameParts {
  return [number, clientName.trim(), /^\d{4}-\d{2}/.exec(startDate)?.[0] ?? '', ''];
}

/**
 * `name` with the suggestion moved on, part by part: a part the admin left empty
 * or never changed from `previous` takes the `next` one; a part they typed stays.
 * The purpose is never suggested, so it is never touched.
 */
export function followSuggestion(name: string, previous: string, next: string): string {
  const parts = splitListName(name);
  const was = splitListName(previous);
  const now = splitListName(next);
  for (let i = 0; i < CONVENTION_FIELDS - 1; i++) {
    if (parts[i] === '' || parts[i] === was[i]) parts[i] = now[i];
  }
  return joinListName(parts);
}

/**
 * The name to offer for a copy of `name`, avoiding the ones already taken.
 *
 * A copy needs a name of its own before it needs anything else: two lists called
 * the same thing is the exact failure the naming convention exists to prevent,
 * and a duplicate that lands at the bottom of a table sorted by creation date,
 * under a name identical to its original, is a duplicate nobody finds.
 *
 * When the name follows the convention and opens with a number, the number moves
 * on: `01 – Acme – 2025-08 – Angels` becomes `02 – …`, which is what an admin
 * would have typed. The padding is kept (`09` → `10`, not `10` → `9`), and lost
 * only when the number outgrows it (`99` → `100`).
 *
 * Anything else gets " (copy)". A blind suffix on a conforming name would break
 * the convention this page teaches, and inventing a number for a name that never
 * had one would claim a sequence that does not exist.
 *
 * `taken` is consulted either way: the next free number, or `(copy 2)`,
 * `(copy 3)`. Comparison is trimmed and case-insensitive, because "Acme" and
 * "acme " are the same list to everyone but a database.
 */
export function duplicateName(name: string, taken: readonly string[] = []): string {
  const used = new Set(taken.map((n) => n.trim().toLowerCase()));
  const free = (candidate: string) => !used.has(candidate.trim().toLowerCase());

  const fields = name.split(SEPARATOR);
  const first = fields[0]?.trim() ?? '';

  if (fields.length >= CONVENTION_FIELDS && /^\d+$/.test(first)) {
    const rest = fields.slice(1).join(NAMING_SEPARATOR);
    const start = Number(first) + 1;
    for (let n = start; n < start + 1000; n++) {
      const candidate = `${String(n).padStart(first.length, '0')}${NAMING_SEPARATOR}${rest}`;
      if (free(candidate)) return candidate;
    }
  }

  const base = `${name.trim()} (copy)`;
  if (free(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${name.trim()} (copy ${n})`;
    if (free(candidate)) return candidate;
  }
  return base;
}
